import { api } from '../api/endpoints.js';
import { haptic } from '../tg.js';

// Telegram Stars top-up bottom sheet.
//
// Usage:
//   const result = await openTopupSheet(parent, { balance: 12, suggested: 50 });
//   // result = { status: 'paid' | 'cancelled' | 'failed' | 'pending' | 'closed', amount?, balance? }
//   // On 'paid' caller should refresh balance via api.getMe() — caller does this (we don't
//   // mutate ctx.state from inside the sheet).
//
// Flow:
//   1. User picks bundle (10/50/100/500) or types custom amount → POST /v1/topup/star/invoice
//   2. Telegram.WebApp.openInvoice(url, callback) — Telegram UI takes over
//   3. callback fires with status; we resolve promise + close sheet
//
// Outside Telegram (browser dev) → openInvoice unavailable → show error.

const BUNDLES = [10, 50, 100, 500];

export function openTopupSheet(parent, opts = {}) {
  return new Promise((resolve) => {
    const { balance = 0, suggested = null } = opts;

    const el = document.createElement('div');
    el.className = 'modal-overlay topup-overlay';
    el.innerHTML = `
      <div class="topup-sheet">
        <div class="topup-handle"></div>
        <div class="topup-header">
          <div class="topup-title">BUY STARS</div>
          <div class="topup-balance">Balance: <b class="neon-gold">${balance}⭐</b></div>
        </div>
        <div class="topup-desc">1 ⭐ Telegram Star = 1 game star</div>
        <div class="topup-bundles">
          ${BUNDLES.map((n) => `
            <button class="topup-bundle ${n === suggested ? 'suggested' : ''}" data-amount="${n}">
              <span class="b-amount">${n}</span>
              <span class="b-star">⭐</span>
            </button>
          `).join('')}
        </div>
        <div class="topup-custom">
          <input class="topup-input" type="number" inputmode="numeric"
                 min="1" max="10000" placeholder="Custom amount (1–10000)" />
          <button class="pg-btn primary topup-buy" disabled>BUY</button>
        </div>
        <button class="topup-close">Cancel</button>
        <div class="topup-status" id="topup-status" style="display:none"></div>
      </div>`;
    parent.appendChild(el);

    const sheetEl = el.querySelector('.topup-sheet');
    requestAnimationFrame(() => sheetEl.classList.add('show'));

    const statusEl = el.querySelector('#topup-status');
    const input = el.querySelector('.topup-input');
    const buyBtn = el.querySelector('.topup-buy');
    const bundleBtns = el.querySelectorAll('.topup-bundle');
    const closeBtn = el.querySelector('.topup-close');

    let selected = null;
    let busy = false;

    const setStatus = (text, kind = 'info') => {
      if (!text) { statusEl.style.display = 'none'; return; }
      statusEl.style.display = 'block';
      statusEl.className = `topup-status kind-${kind}`;
      statusEl.textContent = text;
    };

    const close = (result) => {
      if (sheetEl.classList.contains('closing')) return;
      sheetEl.classList.add('closing');
      setTimeout(() => {
        el.remove();
        resolve(result);
      }, 220);
    };

    const setSelected = (amount) => {
      selected = amount;
      bundleBtns.forEach((b) => {
        b.classList.toggle('active', Number(b.dataset.amount) === amount);
      });
      buyBtn.disabled = !(amount > 0 && amount <= 10000);
    };

    bundleBtns.forEach((b) => {
      b.onclick = () => {
        if (busy) return;
        haptic('selection');
        input.value = '';
        setSelected(Number(b.dataset.amount));
      };
    });

    input.oninput = () => {
      if (busy) return;
      bundleBtns.forEach((b) => b.classList.remove('active'));
      const n = parseInt(input.value, 10);
      if (Number.isFinite(n) && n > 0) setSelected(n);
      else setSelected(null);
    };

    buyBtn.onclick = async () => {
      if (busy || !selected) return;
      busy = true;
      buyBtn.disabled = true;
      buyBtn.textContent = 'CREATING…';
      setStatus('Preparing invoice…', 'info');

      let invoice;
      try {
        invoice = await api.createStarInvoice(selected);
      } catch (e) {
        busy = false;
        buyBtn.disabled = false;
        buyBtn.textContent = 'BUY';
        setStatus(`Failed: ${e.message || 'create invoice error'}`, 'error');
        return;
      }

      const tg = window.Telegram?.WebApp;
      if (!tg || typeof tg.openInvoice !== 'function') {
        busy = false;
        buyBtn.disabled = false;
        buyBtn.textContent = 'BUY';
        setStatus('Telegram payments not available here. Open inside Telegram.', 'error');
        return;
      }

      setStatus('Opening Telegram payment…', 'info');
      buyBtn.textContent = 'PROCESSING…';

      tg.openInvoice(invoice.invoice_link, (status) => {
        // status: 'paid' | 'cancelled' | 'failed' | 'pending'
        if (status === 'paid') {
          haptic('success');
          setStatus(`Paid ${selected}⭐ — adding to balance…`, 'success');
          // BE webhook credits the ledger; small delay so getMe() picks up new balance.
          setTimeout(() => close({ status: 'paid', amount: selected, orderID: invoice.order_id }), 600);
        } else if (status === 'cancelled') {
          haptic('warning');
          setStatus('Cancelled', 'warn');
          busy = false;
          buyBtn.disabled = false;
          buyBtn.textContent = 'BUY';
        } else if (status === 'failed') {
          haptic('error');
          setStatus('Payment failed', 'error');
          busy = false;
          buyBtn.disabled = false;
          buyBtn.textContent = 'BUY';
        } else {
          // pending
          setStatus('Payment pending — check balance later', 'warn');
          setTimeout(() => close({ status: 'pending', amount: selected, orderID: invoice.order_id }), 800);
        }
      });
    };

    closeBtn.onclick = () => close({ status: 'closed' });

    // Tap outside to close (only when idle)
    el.onclick = (ev) => {
      if (ev.target === el && !busy) close({ status: 'closed' });
    };

    // Pre-select suggested
    if (suggested && BUNDLES.includes(suggested)) {
      setSelected(suggested);
    } else if (suggested && suggested > 0) {
      input.value = suggested;
      setSelected(suggested);
    }
  });
}
